// useDoctorRegistrationOpen — can the doctor registration page offer a tenant?
//
// WHY (owner, 1 Oct 2026, "Concept 2"): doctor self-registration has two doors
// on one form — an INVITATION link (?invitationCode=, always allowed) and a
// VOLUNTARY sign-up at an organisation that allows it. The platform flag
// `auth.doctorRegistration` (/feature/by-keys) is no longer the authority for
// doctors; each tenant says so itself (`doctorRegistration` on its
// /tenants/public row). The route guard, the Home hero button, RegisterChoice
// and DoctorRegister itself all ask the same question, so it is answered here,
// once per page load:
//
//   - on a tenant-bound host (<tenant>.<base domain>), the plain
//     GET /tenants/public answers that one tenant — its own flag decides;
//   - otherwise GET /tenants/public?doctorRegistration=1 lists the active
//     tenants that allow it — open when the list is not empty.
//
// The answer is cached at module level (one request, however many components
// ask); a failed lookup is not cached, so the next mount tries again.

import { useEffect, useState } from 'react';
import createAuthedAxios from './useAxiosAuth';
import baseURL from '../utils/baseURL';
import { isTenantHost } from '../utils/tenantHost';

let cachedValue = null;   // { hostTenant, tenants } once settled
let pending = null;       // the in-flight lookup, shared by every caller

const EMPTY = { hostTenant: null, tenants: [] };

async function lookup() {
    const api = createAuthedAxios();
    if (isTenantHost()) {
        try {
            const res = await api.get(`${baseURL}/tenants/public`);
            const list = res?.data?.tenants || [];
            // Exactly one row = this host's tenant. (A stale token in this
            // browser can make the plain call answer every tenant; that is not
            // a host answer, so fall through to the filtered list.)
            if (list.length === 1) return { hostTenant: list[0], tenants: [] };
        } catch { /* fall through to the list */ }
    }
    const res = await api.get(`${baseURL}/tenants/public?doctorRegistration=1`);
    // Strictly `true`: a server that ignored the filter answers rows with no
    // flag at all, and those must not read as "open".
    const tenants = (res?.data?.tenants || []).filter((tn) => tn && tn.doctorRegistration === true);
    return { hostTenant: null, tenants };
}

/** The shared lookup — resolves `{ hostTenant, tenants }`, never rejects. */
export function loadDoctorRegistrationTenants() {
    if (cachedValue) return Promise.resolve(cachedValue);
    if (!pending) {
        pending = lookup()
            .then((value) => { cachedValue = value; return value; })
            .catch(() => EMPTY)
            .finally(() => { pending = null; });
    }
    return pending;
}

/** Tests only — forget the cached answer. */
export function resetDoctorRegistrationCache() {
    cachedValue = null;
    pending = null;
}

/** Open = the host tenant allows it, or some tenant on the list does. */
export const isRegistrationOpen = ({ hostTenant, tenants }) =>
    hostTenant ? hostTenant.doctorRegistration === true : (tenants || []).length > 0;

/**
 * @returns {{ loading: boolean, open: boolean, hostTenant: object|null, tenants: object[] }}
 */
export default function useDoctorRegistrationOpen() {
    const [value, setValue] = useState(cachedValue);
    useEffect(() => {
        if (value) return undefined;
        let alive = true;
        loadDoctorRegistrationTenants().then((v) => { if (alive) setValue(v); });
        return () => { alive = false; };
    }, [value]);
    const v = value || EMPTY;
    return { loading: !value, open: value ? isRegistrationOpen(v) : false, hostTenant: v.hostTenant, tenants: v.tenants };
}
